/**
 * Types et utilitaires de layout partagés entre Présentation et Composition.
 *
 * - SlideSpan : span sur la grille 12 colonnes (3/4/6/8/12). Max 4 blocs/ligne.
 * - SlideWidth : type historique qui accepte les alias legacy ('full'/'half'/'third').
 * - toSpan / spanToClass : conversions vers le DOM.
 * - inferDefaultWidth : heuristique de largeur par défaut selon le type de section.
 * - autoAdjustSpans : rééquilibre les spans d'une ligne logique après un drop DnD.
 */

export type SlideSpan = 3 | 4 | 6 | 8 | 12;
export type SlideWidth = SlideSpan | 'full' | 'half' | 'third';

export type EditorMode = 'edition' | 'presentation';

export type SectionType =
  | 'hero'
  | 'kpi'
  | 'text'
  | 'chart'
  | 'capture'
  | 'table'
  | 'timeline'
  | 'quote';

export type Section = {
  id: string;
  type: SectionType;
  width?: SlideWidth;
  /**
   * Si true, la section démarre EXPLICITEMENT une nouvelle ligne logique
   * (empêche le pack auto de l'absorber avec ses voisines du dessus).
   * Posé par le handler de drop quand l'utilisateur lâche verticalement.
   * Effacé (false) quand il lâche latéralement.
   */
  newRow?: boolean;
  title?: string;
  body?: string;
  kpis?: Array<{ label: string; value: string; trend?: string }>;
  chart?: {
    kind: 'donut' | 'bar' | 'line' | 'radar' | 'sankey';
    data: any[];
    xKey?: string;
    yKey?: string;
    label?: string;
    series?: Array<{ key: string; label: string; color?: string }>;
    unit?: string;
    targets?: any[];
    stacked?: boolean;
    horizontal?: boolean;
  };
  capture_id?: number;
  table?: { columns: string[]; rows: string[][] };
  timeline?: { items: Array<{ date: string; title: string; text?: string }> };
  quote?: string;
};

export function toSpan(w: SlideWidth | undefined): SlideSpan {
  if (typeof w === 'number') return w as SlideSpan;
  if (w === 'full') return 12;
  if (w === 'half') return 6;
  if (w === 'third') return 4;
  return 12;
}

export function spanToClass(w: SlideWidth | undefined): string {
  if (w === 12 || w === 'full' || w == null) return 'slide-span-12';
  if (w === 8) return 'slide-span-8';
  if (w === 6 || w === 'half') return 'slide-span-6';
  if (w === 4 || w === 'third') return 'slide-span-4';
  if (w === 3) return 'slide-span-3';
  return 'slide-span-12';
}

export function isValidWidth(w: any): w is SlideWidth {
  if (typeof w === 'number') return w === 3 || w === 4 || w === 6 || w === 8 || w === 12;
  return w === 'full' || w === 'half' || w === 'third';
}

/** Heuristique de largeur par défaut selon le type de section et la quantité de contenu. */
export function inferDefaultWidth(s: any): SlideSpan {
  const t = s?.type;
  if (t === 'hero') return 12;
  if (t === 'kpi') {
    const n = Array.isArray(s?.kpis) ? s.kpis.length : 0;
    return n <= 2 ? 6 : 12;
  }
  if (t === 'chart') {
    const kind = s?.chart?.kind;
    const data = Array.isArray(s?.chart?.data) ? s.chart.data.length : 0;
    if (kind === 'donut' || kind === 'radar') return 6;
    if (kind === 'bar' && data > 8) return 12;
    if (kind === 'bar') return 6;
    if (kind === 'sankey') return 12;
    return 6;
  }
  if (t === 'timeline') return 12;
  if (t === 'capture') return 6;
  if (t === 'quote') return 6;
  if (t === 'table') {
    const rows = Array.isArray(s?.table?.rows) ? s.table.rows.length : 0;
    const cols = Array.isArray(s?.table?.columns) ? s.table.columns.length : 0;
    return rows > 4 || cols > 3 ? 12 : 6;
  }
  if (t === 'text') {
    const len = String(s?.body ?? '').length;
    return len > 280 ? 12 : 6;
  }
  return 12;
}

/**
 * Packing automatique des blocs en lignes de 1 à 4 blocs, avec spans équilibrés
 * (12/6/4/3 selon le nombre de blocs sur la ligne).
 *
 * Règles :
 *   - Les blocs `hero` prennent toute la ligne (span 12) et brisent le groupe.
 *   - Les blocs successifs non-hero se groupent jusqu'à 4 maximum par ligne.
 *   - Un bloc avec `newRow: true` brise le groupe : il commence une nouvelle
 *     ligne (posé par le handler de drop sur lâcher vertical).
 *   - Chaque bloc d'une ligne reçoit la même largeur = 12 / N.
 *
 * On IGNORE les `width` actuels : le packing se fait purement sur l'ordre et
 * les drapeaux explicites. C'était le bug historique (respecter width=12 →
 * chaque bloc restait seul sur sa ligne). Le bouton "largeur" par bloc reste
 * opérationnel, il n'est simplement plus consulté par le packing auto.
 */
export function autoAdjustSpans<T extends { type: SectionType; width?: SlideWidth; newRow?: boolean }>(
  sections: T[]
): T[] {
  const out = sections.map((s) => ({ ...s }));
  // Diagnostic — visible dans la console pour vérifier l'entrée.
  // eslint-disable-next-line no-console
  console.debug('[autoAdjust] input', out.map((s: any) => ({ id: s.id, type: s.type, width: s.width, newRow: s.newRow })));

  let i = 0;
  const rows: number[][] = []; // pour le diagnostic
  while (i < out.length) {
    if (out[i].type === 'hero') {
      out[i].width = 12;
      rows.push([i]);
      i++;
      continue;
    }
    // Groupe : jusqu'à 4 blocs consécutifs non-hero, tant qu'aucun n'a newRow.
    let j = i + 1;
    while (j < out.length && (j - i) < 4) {
      if (out[j].type === 'hero') break;
      if (out[j].newRow) break;
      j++;
    }
    const rowIdxs: number[] = [];
    for (let k = i; k < j; k++) rowIdxs.push(k);
    const rowCount = j - i;
    const target: SlideSpan =
      rowCount === 1 ? 12 : rowCount === 2 ? 6 : rowCount === 3 ? 4 : 3;
    for (let k = i; k < j; k++) out[k].width = target;
    rows.push(rowIdxs);
    i = j;
  }
  // eslint-disable-next-line no-console
  console.debug('[autoAdjust] rows', rows, 'output', out.map((s: any) => ({ id: s.id, width: s.width })));
  return out;
}
